import { createFileRoute, useRouter } from '@tanstack/react-router'
import type { JSX } from 'react'

import { useAccess } from '../components/access-guard.tsx'
import { Link } from '../design-system/link.tsx'
import { Panel } from '../design-system/panel.tsx'
import { viewablePages } from '../lib/access.ts'

export const Route = createFileRoute('/')({
  component: HomePage
})

/**
 * PageList — every page this viewer may open.
 *
 * Server-driven, like the rail. An empty list is a real answer, not a failure:
 * a signed-in employee with no page open to them sees nothing here.
 *
 * @returns The list, or the state that stands in for it.
 */
function PageList(): JSX.Element {
  const { data, isPending } = useAccess()
  const router = useRouter()

  if (isPending) {
    return (
      <p data-testid="home-loading" className="text-muted-foreground text-sm">
        {'Loading your pages…'}
      </p>
    )
  }

  const pages = data ? viewablePages(data) : []

  if (pages.length === 0) {
    return (
      <p data-testid="home-empty" className="text-muted-foreground text-sm">
        {'No pages are open to you yet.'}
      </p>
    )
  }

  return (
    <ul data-testid="home-pages" className="mt-6 flex flex-col gap-3">
      {pages.map(page => (
        <li
          key={page.id}
          className="border-border rounded-lg border p-4 shadow-xs"
        >
          <Link
            data-testid={`home-page-${page.id}`}
            // A registry path is a string at runtime — see the note in
            // __root.tsx for why this is not a TanStack Link.
            href={page.path}
            onNavigate={() => {
              router.history.push(page.path)
            }}
            className="text-base font-medium hover:underline"
          >
            {page.title}
          </Link>
          <p className="text-muted-foreground font-mono text-xs">{page.path}</p>
        </li>
      ))}
    </ul>
  )
}

/**
 * HomePage — what an operator lands on.
 *
 * @returns The home surface.
 */
function HomePage(): JSX.Element {
  return (
    <Panel
      data-testid="home-panel"
      title="ui-internal"
      description="The single shared internal webapp — every BlytzPay internal app lives inside it."
    >
      <p data-testid="home-body" className="text-muted-foreground text-sm">
        {'Pick an app from the rail.'}
      </p>
      <PageList />
    </Panel>
  )
}
